/**
 * Dashboard tenant context: selected tenant for platform admins on the home dashboard.
 *
 * Pure derivative of the global TenantContext:
 * - Platform admins: reflects currentTenant.id, or 'all' when the "All Tenants"
 *   option is active (currentTenant === null, set via the header switcher on the home page).
 * - Regular users: reflects currentTenant.id (backend always scopes to their tenant).
 *
 * The "All Tenants" option is only available in the app-header tenant switcher when the
 * user is on the home page ('/').  Navigating away auto-reverts to the home tenant
 * (handled in AppLayout).
 */

import React, { createContext, useContext, useMemo, useCallback, type ReactNode } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTenant } from '@contexts/TenantContext'
import { useAssumedRole } from '@contexts/AssumedRoleContext'
import { platformAdminApi } from '@services/api'
import type { Tenant } from '@types/tenant'

export interface DashboardTenantContextValue {
  /** Selected tenant ID for dashboard metrics. 'all' means aggregate view (platform admin only). */
  dashboardTenantId: string | null
  /** No-op: tenant selection is driven by the global header switcher. */
  setDashboardTenantId: (id: string | null) => void
}

const defaultValue: DashboardTenantContextValue = {
  dashboardTenantId: null,
  setDashboardTenantId: () => {},
}

const DashboardTenantContext = createContext<DashboardTenantContextValue>(defaultValue)

export function DashboardTenantProvider({ children }: { children: ReactNode }) {
  const { currentTenant, isPlatformAdmin } = useTenant()
  const { assumedRole } = useAssumedRole()
  // Switch Role is never platform admin, so Home must not keep requesting "all".
  const effectiveIsPlatformAdmin = isPlatformAdmin && !assumedRole

  const dashboardTenantId = useMemo(() => {
    if (effectiveIsPlatformAdmin) return currentTenant?.id ?? 'all'
    return currentTenant?.id ?? null
  }, [currentTenant, effectiveIsPlatformAdmin])

  const value = useMemo<DashboardTenantContextValue>(
    () => ({ dashboardTenantId, setDashboardTenantId: () => {} }),
    [dashboardTenantId]
  )

  return (
    <DashboardTenantContext.Provider value={value}>
      {children}
    </DashboardTenantContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook must live with the provider
export function useDashboardTenant(): DashboardTenantContextValue {
  return useContext(DashboardTenantContext)
}

const TENANT_PAGE_SIZE = 25

/**
 * Lazy platform-admin tenant list for the header switcher.
 * Uses infinite query (25-item pages) so tenants load on scroll.
 * Query stays disabled until `enabled` is true (dropdown opened).
 */
// eslint-disable-next-line react-refresh/only-export-components -- lazy query hook used by TenantSwitcher
export function useLazyPlatformTenantList(
  enabled: boolean,
  search: string = '',
): {
  tenants: Tenant[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  totalCount: number
} {
  const query = useInfiniteQuery({
    queryKey: ['platform-admin', 'tenant-list', search],
    queryFn: async ({ pageParam = 1 }) => {
      return platformAdminApi.listTenants({
        page: pageParam as number,
        page_size: TENANT_PAGE_SIZE,
        search: search || undefined,
        status: 'active',
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.items.length < TENANT_PAGE_SIZE) return undefined
      const totalLoaded = allPages.reduce((acc, p) => acc + p.items.length, 0)
      if (totalLoaded >= lastPage.total) return undefined
      return allPages.length + 1
    },
    enabled,
    staleTime: 60_000,
  })

  const tenants = useMemo(() => {
    if (!query.data?.pages) return []
    const seen = new Set<string>()
    const list: Tenant[] = []
    for (const page of query.data.pages) {
      for (const item of page.items) {
        if (!seen.has(item.id)) {
          seen.add(item.id)
          list.push(item)
        }
      }
    }
    return list
  }, [query.data])

  const totalCount = query.data?.pages[0]?.total ?? 0

  const { fetchNextPage: queryFetchNextPage } = query
  const fetchNextPage = useCallback(() => {
    void queryFetchNextPage()
  }, [queryFetchNextPage])

  return {
    tenants,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage,
    totalCount,
  }
}

